import {
  faChevronDown,
  faChevronLeft,
  faPlus,
  faTableColumns,
} from "@fortawesome/pro-regular-svg-icons";
import { Menu, MenuButton, MenuItem, MenuItems } from "@headlessui/react";
import { AnimatePresence } from "framer-motion";
import { FC, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useOnClickOutside } from "usehooks-ts";

import { FlowVersionFlowChild } from "src/api/flowTypes";
import { DatasetColumnGroup, GLOBAL_FEATURES_TYPE } from "src/api/types";
import { Button } from "src/base-components/Button";
import { Icon } from "src/base-components/Icon";
import { MotionDropdownPanel } from "src/base-components/MotionDropdownPanel";
import { useAvailableIntegrationNodes } from "src/datasets/DatasetTable/hooks";
import { DatasetContext } from "src/datasets/DatasetTable/types";
import { useSchemas } from "src/datasets/utils";
import { DropdownItem } from "src/design-system/Dropdown";
import { EmptyState as BaseEmptyState } from "src/design-system/EmptyState";
import { useWorkspaceFeatureGates } from "src/hooks/useWorkspaceFeatureGates";

type MenuElement = {
  key: string;
  value: string;
  children?: MenuElement[];
};

const AddColumnMenu: FC<{
  onAddColumn: (name: string, group: DatasetColumnGroup) => void;
  onAddAdditionalColumn: () => void;
  inputColumns: string[];
  outputColumns: string[];
  mockColumns: string[];
  context: DatasetContext;
}> = ({
  onAddColumn,
  onAddAdditionalColumn,
  inputColumns,
  outputColumns,
  mockColumns,
  context,
}) => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [isOpen, setIsOpen] = useState(searchParams.has("openAddColumnMenu"));
  const [view, setView] = useState<string>("");

  const isAuthoringDatasetContext = context === "authoring";

  useEffect(() => {
    setSearchParams((searchParams) => {
      searchParams.delete("openAddColumnMenu");
      return searchParams;
    });
  }, [setSearchParams]);

  const menu: MenuElement[] = useMemo(() => {
    const items = [];

    if (inputColumns.length > 0) {
      items.push({
        key: "input_columns",
        value: "Add input column",
        children: inputColumns.map((column) => ({
          key: column,
          value: column,
        })),
      });
    }

    if (isAuthoringDatasetContext && mockColumns.length > 0) {
      items.push({
        key: "mock_columns",
        value: "Add mock column",
        children: mockColumns.map((column) => ({
          key: column,
          value: column,
        })),
      });
    }

    if (isAuthoringDatasetContext && outputColumns.length > 0) {
      items.push({
        key: "output_columns",
        value: "Add expected column",
        children: outputColumns.map((column) => ({
          key: column,
          value: column,
        })),
      });
    }

    items.push({
      key: "additional_column",
      value: "Add additional column",
    });

    return items;
  }, [inputColumns, mockColumns, outputColumns, isAuthoringDatasetContext]);

  const handleClick =
    (element: MenuElement) => (e: React.MouseEvent<HTMLDivElement>) => {
      if (element.children) {
        e.preventDefault();
        setView(element.key);
        return;
      }

      if (element.key === "additional_column") {
        onAddAdditionalColumn();
        return;
      }

      setView("");
      onAddColumn(element.key, view as DatasetColumnGroup);
    };

  const menuElements =
    view === ""
      ? menu
      : (menu.find((item) => item.key === view)?.children ?? menu);

  const ref = useRef<HTMLDivElement>(null);

  useOnClickOutside(ref, () => {
    setIsOpen(false);
  });

  return (
    <Menu as="div" className="relative">
      <MenuButton
        as={Button}
        iconLeft={faPlus}
        iconRight={faChevronDown}
        variant="secondary"
        onClick={() => setIsOpen((isOpen) => !isOpen)}
      >
        Add column
      </MenuButton>
      <AnimatePresence initial={false}>
        {isOpen && (
          <MenuItems
            ref={ref}
            anchor={{
              to: "bottom",
              gap: 8,
              padding: 12,
            }}
            as={MotionDropdownPanel}
            static
          >
            {view !== "" && (
              <MenuItem
                as={DropdownItem}
                onClick={(e) => {
                  e.preventDefault();
                  setView("");
                }}
              >
                <div className="flex items-center gap-x-2">
                  <Icon icon={faChevronLeft} size="sm" />
                  Back
                </div>
              </MenuItem>
            )}
            <div className="decideScrollbar max-h-48">
              {menuElements.map((element) => (
                <MenuItem
                  key={element.key}
                  as={DropdownItem}
                  onClick={handleClick(element)}
                >
                  <div className="flex items-center gap-x-2">
                    <Icon color="text-indigo-500" icon={faPlus} size="sm" />
                    {element.value}
                  </div>
                </MenuItem>
              ))}
            </div>
          </MenuItems>
        )}
      </AnimatePresence>
    </Menu>
  );
};

export const EmptyState: React.FC<{
  version?: FlowVersionFlowChild;
  onAddColumn: (name: string, group: DatasetColumnGroup) => void;
  onAddAdditionalColumn: () => void;
  context: DatasetContext;
}> = ({ version, onAddColumn, onAddAdditionalColumn, context }) => {
  const integrationNodes = useAvailableIntegrationNodes(version);
  const featureGates = useWorkspaceFeatureGates();
  const schemas = useSchemas(version);

  return (
    <BaseEmptyState
      action={
        <AddColumnMenu
          context={context}
          inputColumns={[
            ...(schemas.input?.properties?.map((prop) => prop.fieldName) ?? []),
            ...(featureGates.featuresEventsEnabled
              ? [GLOBAL_FEATURES_TYPE]
              : []),
          ]}
          mockColumns={integrationNodes.map((node) => node.name)}
          outputColumns={
            schemas.output?.properties?.map((prop) => prop.fieldName) ?? []
          }
          onAddAdditionalColumn={onAddAdditionalColumn}
          onAddColumn={onAddColumn}
        />
      }
      description="Add a column to this dataset"
      headline="Add Column"
      icon={faTableColumns}
    />
  );
};
